// The quick go-to (Ctrl+/, OEM-213): what a query finds on the board this tab is on, and in what order,
// plus the keyboard rules of its window. Pure: no DOM, no React, so it is tested on its own
// (apps/web/test/quickfind.test.ts). The window itself is components/QuickGoTo.tsx.
//
// What it accepts: a number ("188": #188, NN-188 in every project, "was NN-188"), a project number
// ("03-188", "3-188", "OEM-03-188"), a # reference ("#188", "OEM-188", GitHub's form), words (every
// word somewhere in the title, the description, the notes, the tags or the project's name), or a
// number with words ("188 label"). It searches the board payload already in the browser, which is
// this tab's board only (and its archive): nothing from another board.

import { parseKey, projectCode, type BoardItem } from "@boem/core/shared";

/** At most this many rows. */
export const QUICK_LIMIT = 20;

/** Where the person is: the project whose board is open, or whose panel has the focus (its # number). */
export interface FindContext {
  project: number | null;
}

/** Why a row matched (and how its number is shown). */
export type FindWhy = "num" | "key" | "project" | "former" | "text" | "recent";

/** Where words matched outside the title: one short line, with the matched words as [start, end) ranges. */
export interface Snippet {
  field: "description" | "note" | "tags" | "project";
  text: string;
  marks: [number, number][];
}

export interface FindHit {
  item: BoardItem;
  why: FindWhy;
  /** a former key it matched by ("03-188"): shown as "was 03-188" */
  former?: string;
  /** where the words matched, when that isn't the title */
  snippet?: Snippet;
}

/** What was typed, read. */
export type FindQuery =
  | { kind: "empty" }
  /** "188": the # and any project's 188. `exact` ("#188", "OEM-188"): that one item. `words`: "188 label" */
  | { kind: "num"; n: number; exact: boolean; words: string[] }
  /** "03-188", "OEM-03-188"; `words`: "03-188 label" */
  | { kind: "key"; projectNo: number; seq: number; words: string[] }
  | { kind: "words"; words: string[] };

/** One character folded: lower case, accents off ("É" -> "e"). */
const foldChar = (c: string) => c.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();
/** Lower case, accents off, so "Café" finds "cafe". */
export const fold = (s: string) => [...s].map(foldChar).join("");

/** A number or a reference on its own: what it means, else null. */
function readRef(s: string): Exclude<FindQuery, { kind: "empty" } | { kind: "words" }> | null {
  const oem = s.match(/^oem-?(\d{1,7})(?:-(\d{1,6}))?$/i);
  if (oem) {
    if (oem[2] != null) {
      const k = parseKey(`${oem[1]}-${oem[2]}`);
      return k ? { kind: "key", ...k, words: [] } : null;
    }
    return Number(oem[1]) > 0 ? { kind: "num", n: Number(oem[1]), exact: true, words: [] } : null;
  }
  const hash = s.match(/^#(\d{1,7})$/);
  if (hash) return Number(hash[1]) > 0 ? { kind: "num", n: Number(hash[1]), exact: true, words: [] } : null;
  if (/^\d{1,7}$/.test(s)) return Number(s) > 0 ? { kind: "num", n: Number(s), exact: false, words: [] } : null;
  const k = parseKey(s);
  return k ? { kind: "key", ...k, words: [] } : null;
}

export function parseQuery(raw: string): FindQuery {
  const s = raw.trim().replace(/^#\s+(?=\d)/, "#");
  if (!s) return { kind: "empty" };
  const whole = readRef(s);
  if (whole) return whole;
  const tokens = s.split(/\s+/).filter(Boolean);
  // "188 label", "label 03-07": the first token that is a number or a reference, the rest as words
  const at = tokens.findIndex((t) => readRef(t) != null);
  if (at >= 0 && tokens.length > 1) {
    const ref = readRef(tokens[at]!)!;
    const words = tokens.filter((_, i) => i !== at).map((w) => fold(w));
    // "#188 label": the # reference doesn't narrow to one item when words come with it
    return ref.kind === "num" ? { ...ref, exact: false, words } : { ...ref, words };
  }
  const words = tokens.map((w) => fold(w.replace(/^#/, ""))).filter(Boolean);
  return words.length ? { kind: "words", words } : { kind: "empty" };
}

/** When anything last happened to an item (the board sends no "updated": the latest of its stamps). */
export function touchedAt(it: BoardItem): number {
  let t = Date.parse(it.addedAt) || 0;
  const at = (s: string | null | undefined) => {
    const v = s ? Date.parse(s) : NaN;
    if (v > t) t = v;
  };
  at(it.closedAt);
  at(it.assignee?.at);
  at(it.developed?.at);
  at(it.deployed?.at);
  at(it.verified?.at);
  at(it.archived?.at);
  for (const n of it.notes) at(n.at);
  for (const h of it.handoffs) at(h.at);
  return t;
}

/** open 0, closed 1, archived 2: the first thing the order looks at. */
export const stateOf = (it: Pick<BoardItem, "status" | "archived">) => (it.archived ? 2 : it.status === "closed" ? 1 : 0);

/** "01-07" for project 1's item 7, from a former key as stored (which may lack the zeros). */
const normKey = (k: string) => {
  const p = parseKey(k);
  return p ? `${projectCode(p.projectNo)}-${String(p.seq).padStart(2, "0")}` : k;
};

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// -- Free text ------------------------------------------------------------------------------

/** How far down a text match is: title first (its start highest), then the description, notes, then tags or the project's name. */
const TEXT_TIER = { titleStart: 0, titleWord: 1, title: 2, description: 3, note: 4, other: 5 } as const;

interface TextMatch {
  tier: number;
  snippet?: Snippet;
}

/**
 * Where word `w` occurs in `text` (folded) from `from`, or -1. A number matches only as a whole number
 * ("5" finds "bay 5", not "$554", "#105" or "B5B"), so a short number doesn't find half the board.
 */
export function findAt(text: string, w: string, from = 0): number {
  if (!/^\d+$/.test(w)) return text.indexOf(w, from);
  for (let i = text.indexOf(w, from); i >= 0; i = text.indexOf(w, i + 1)) {
    if (!/[\p{L}\p{N}]/u.test(text[i - 1] ?? "") && !/[\p{L}\p{N}]/u.test(text[i + w.length] ?? "")) return i;
  }
  return -1;
}
const has = (text: string, w: string) => findAt(text, w) >= 0;

/** The text an item can be found by, field by field (the project's name: the one it is in, or its own for a project). */
function fields(it: BoardItem) {
  return {
    title: it.title,
    description: it.body ?? "",
    notes: it.notes.map((n) => n.body ?? ""),
    tags: it.tags.join(" "),
    project: it.project?.title ?? "",
  };
}

/**
 * Does every word occur somewhere in the item's text? Then how far down it ranks: all in the title
 * (the title starting with the query highest, then every word starting a title word), else the
 * furthest field it needed (description, then notes, then tags and the project's name), with a
 * snippet of the first field outside the title that holds a word the title doesn't.
 */
export function textMatch(it: BoardItem, words: string[]): TextMatch | null {
  if (!words.length) return null;
  const f = fields(it);
  const title = fold(f.title);
  const desc = fold(f.description);
  const notes = f.notes.map(fold);
  const other = fold(`${f.tags} ${f.project}`);
  const missing = words.filter((w) => !has(title, w));
  if (!missing.length) {
    const whole = words.join(" ");
    if (title.startsWith(whole) || title.startsWith(words[0]!)) return { tier: TEXT_TIER.titleStart };
    const starts = words.every((w) => new RegExp(`(^|[^\\p{L}\\p{N}])${esc(w)}`, "u").test(title));
    return { tier: starts ? TEXT_TIER.titleWord : TEXT_TIER.title };
  }
  let tier: number = TEXT_TIER.title;
  for (const w of missing) {
    if (has(desc, w)) tier = Math.max(tier, TEXT_TIER.description);
    else if (notes.some((n) => has(n, w))) tier = Math.max(tier, TEXT_TIER.note);
    else if (has(other, w)) tier = Math.max(tier, TEXT_TIER.other);
    else return null;
  }
  // the snippet: the first field (description, a note, the tags, the project's name) holding a missing word
  const w0 = missing[0]!;
  const source: [Snippet["field"], string] | null = has(desc, w0)
    ? ["description", f.description]
    : (() => {
        const i = notes.findIndex((n) => has(n, w0));
        if (i >= 0) return ["note", f.notes[i]!] as [Snippet["field"], string];
        if (has(fold(f.tags), w0)) return ["tags", f.tags] as [Snippet["field"], string];
        return ["project", f.project] as [Snippet["field"], string];
      })();
  return { tier, snippet: snippetOf(source[0], source[1], words) };
}

/** At most this many characters in a snippet. */
export const SNIPPET_LEN = 90;

/**
 * One line of `text` around the first of `words` (folded), at most SNIPPET_LEN characters, "…" where
 * it was cut, with every word's occurrences in it marked. Line breaks become spaces.
 */
export function snippetOf(field: Snippet["field"], text: string, words: string[], max = SNIPPET_LEN): Snippet {
  const line = text.replace(/\s+/g, " ").trim();
  // fold character by character, keeping where each folded character came from
  const chars = [...line];
  let folded = "";
  const from: number[] = [];
  let pos = 0;
  for (const c of chars) {
    const fc = foldChar(c);
    for (let k = 0; k < fc.length; k++) from.push(pos);
    folded += fc;
    pos += c.length;
  }
  from.push(pos);
  const first = Math.min(...words.map((w) => findAt(folded, w)).filter((i) => i >= 0), Infinity);
  const firstAt = Number.isFinite(first) ? from[first]! : 0;
  // start a little before the first match, on a word boundary when there is one
  let start = Math.max(0, firstAt - Math.floor(max / 3));
  if (start > 0) {
    const sp = line.indexOf(" ", start);
    if (sp >= 0 && sp < firstAt) start = sp + 1;
  }
  let end = Math.min(line.length, start + max);
  if (end < line.length) {
    const sp = line.lastIndexOf(" ", end);
    if (sp > firstAt + 8) end = sp;
  }
  const lead = start > 0 ? "…" : "";
  const text2 = `${lead}${line.slice(start, end)}${end < line.length ? "…" : ""}`;
  const marks: [number, number][] = [];
  for (const w of words) {
    for (let i = findAt(folded, w); i >= 0; i = findAt(folded, w, i + Math.max(1, w.length))) {
      const a = from[i]!;
      const b = from[i + w.length] ?? line.length;
      if (a < start || b > end) continue;
      marks.push([a - start + lead.length, b - start + lead.length]);
    }
  }
  marks.sort((a, b) => a[0] - b[0]);
  // overlapping marks (one word inside another) merge
  const merged: [number, number][] = [];
  for (const m of marks) {
    const last = merged[merged.length - 1];
    if (last && m[0] <= last[1]) last[1] = Math.max(last[1], m[1]);
    else merged.push([...m]);
  }
  return { field, text: text2, marks: merged };
}

// -- The ranking ----------------------------------------------------------------------------

interface Ranked extends FindHit {
  state: number;
  tier: number;
  t: number;
}

/**
 * The rows for `query` among `items` (this board's, the archive's included), best first, at most `limit`.
 *
 * Order: open items first, then closed ones, then archived ones (an open 02-188 comes before a closed
 * #188); within each of those:
 * - "#188" or "OEM-188": that one item (a # reference names one item);
 * - "03-188": that project number, then the item that was 03-188;
 * - a number on the main board: the item whose # it is, then the project with that number, then
 *   every project's NN-188, then "was NN-188", then items whose text holds the number;
 * - a number inside a project (its board, or its panel in focus): that project's NN-188, then the #,
 *   then the project with that number, then the other projects' NN-188, "was NN-188", text;
 * - a number with words ("188 label"): the number's matches whose text holds the words, in the
 *   number's order, then the number's other matches, then items whose text holds all of it;
 * - words: in the title (the title starting with the query highest, then every word starting a
 *   title word, then anywhere in it), then the description, then notes, then tags or the project's name;
 * - nothing typed: the most recently updated open items.
 * The most recently updated first where those are equal.
 */
export function quickFind(items: readonly BoardItem[], raw: string, ctx: FindContext = { project: null }, limit = QUICK_LIMIT): FindHit[] {
  const q = parseQuery(raw);
  const out: Ranked[] = [];
  const seen = new Set<number>();
  const add = (item: BoardItem, why: FindWhy, tier: number, extra: { former?: string; snippet?: Snippet } = {}) => {
    if (seen.has(item.num)) return;
    seen.add(item.num);
    out.push({ item, why, tier, state: stateOf(item), t: touchedAt(item), ...(extra.former ? { former: extra.former } : {}), ...(extra.snippet ? { snippet: extra.snippet } : {}) });
  };
  // a reference's own matches: `tier` its place in the number's order; words, when given, split
  // them into those whose text holds the words (first) and the rest (after every one of those)
  const refHit = (item: BoardItem, why: FindWhy, tier: number, words: string[], former?: string) => {
    if (!words.length) return add(item, why, tier, { former });
    const m = textMatch(item, words);
    add(item, why, m ? tier : 10 + tier, { former, snippet: m?.snippet });
  };
  const textHits = (words: string[], base: number) => {
    for (const it of items) {
      const m = textMatch(it, words);
      if (m) add(it, "text", base + m.tier, { snippet: m.snippet });
    }
  };

  if (q.kind === "empty") {
    for (const it of items) if (!it.archived) add(it, "recent", 0);
  } else if (q.kind === "num" && q.exact) {
    for (const it of items) if (it.num === q.n) add(it, "num", 0);
  } else if (q.kind === "num") {
    const inProject = ctx.project != null;
    for (const it of items) {
      if (it.num === q.n) refHit(it, "num", inProject ? 1 : 0, q.words);
      else if (it.key?.seq === q.n) refHit(it, "key", inProject && it.key.projectNum === ctx.project ? 0 : 3, q.words);
      else if (it.kind === "project" && it.projectNo === q.n) refHit(it, "project", 2, q.words);
      else {
        const was = it.formerKeys.find((k) => parseKey(k)?.seq === q.n);
        if (was) refHit(it, "former", 4, q.words, normKey(was));
      }
    }
    // the number as text (a part number in a title, a note): after every reference
    textHits([String(q.n), ...q.words], 20);
  } else if (q.kind === "key") {
    for (const it of items) {
      if (it.key && it.key.projectNo === q.projectNo && it.key.seq === q.seq) refHit(it, "key", 0, q.words);
      else {
        const was = it.formerKeys.find((k) => {
          const p = parseKey(k);
          return p?.projectNo === q.projectNo && p.seq === q.seq;
        });
        if (was) refHit(it, "former", 1, q.words, normKey(was));
      }
    }
  } else {
    textHits(q.words, 0);
  }

  out.sort((a, b) => a.state - b.state || a.tier - b.tier || b.t - a.t || a.item.num - b.item.num);
  return out.slice(0, limit).map(({ item, why, former, snippet }) => ({ item, why, ...(former ? { former } : {}), ...(snippet ? { snippet } : {}) }));
}

/** A row's number in its usual form: "01-07 · #185", "Project 03 · #172", "#188" (and "was 03-188" when that is what matched). */
export function hitNumber(h: FindHit): string {
  const it = h.item;
  const parts = [it.kind === "project" && it.projectNo != null ? `Project ${projectCode(it.projectNo)}` : (it.key?.label ?? null), `#${it.num}`].filter(Boolean);
  if (h.former) parts.push(`was ${h.former}`);
  return parts.join(" · ");
}

// -- The window's keys ------------------------------------------------------------------------

/** What a key press is in the event's terms (KeyboardEvent has all of these). */
export interface KeyLike {
  key: string;
  code?: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey?: boolean;
  isComposing?: boolean;
}

/** Ctrl+/ (Cmd+/ on a Mac; either works anywhere). */
export const isGoToShortcut = (e: KeyLike) => (e.ctrlKey || e.metaKey) && !e.altKey && !e.isComposing && (e.key === "/" || e.code === "Slash");

/** "Ctrl+/" or "⌘/": what the tooltip and the window's foot say. */
export const shortcutLabel = (mac: boolean) => (mac ? "⌘/" : "Ctrl+/");

/** A second Ctrl+/ within this long, while typing in another field, opens it anyway. */
export const AGAIN_MS = 1200;

/**
 * Whether Ctrl+/ opens the window now. Typing in another field: not the first press (the field keeps
 * it), only the same keys pressed again soon after. `last`: when the previous press in a field was.
 */
export function shortcutOpens(typing: boolean, now: number, last: number | null): boolean {
  if (!typing) return true;
  return last != null && now - last <= AGAIN_MS;
}

/** What a key does in the window: move the highlight (wrapping), go (Shift: always in a panel), close, or nothing. */
export type GoKeyAction = { move: number } | { go: number; panel: boolean } | { close: true } | null;

export function goToKey(e: Pick<KeyLike, "key" | "shiftKey" | "isComposing">, active: number, count: number): GoKeyAction {
  if (e.isComposing) return null;
  switch (e.key) {
    case "Escape":
      return { close: true };
    case "ArrowDown":
      return count ? { move: (active + 1 + count) % count } : null;
    case "ArrowUp":
      return count ? { move: (active - 1 + count) % count } : null;
    case "Enter":
      return count && active >= 0 && active < count ? { go: active, panel: !!e.shiftKey } : null;
    default:
      return null;
  }
}

/**
 * Where going takes it: scroll to its card when the board on screen (column 0) shows it, else a
 * panel beside the board. Shift+Enter: a panel always.
 */
export const goToWhere = (onBoard: boolean, shift: boolean): "scroll" | "panel" => (onBoard && !shift ? "scroll" : "panel");
